import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, Clock, Mail, Loader2, FileText, Code2, AlertTriangle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ValidationCheck {
  robotsTxtAccessible: boolean;
  sitemapUrlPresent: boolean;
  sitemapUrlCorrect: boolean;
  sitemapAccessible: boolean;
  sitemapValidXml: boolean;
  sitemapUrlCount: number;
  disallowRulesCorrect: boolean;
}

interface ValidationReport {
  valid: boolean;
  timestamp: string;
  checks: ValidationCheck;
  issues: string[];
  recommendations: string[];
}

interface ValidationLog {
  id: string;
  timestamp: string;
  valid: boolean;
  checks: ValidationCheck;
  issues: string[];
  recommendations: string[];
  email_sent: boolean;
  email_sent_at: string | null;
}

interface MetaValidationLog {
  id: string;
  timestamp: string;
  url: string;
  valid: boolean;
  title_length_ok: boolean;
  description_length_ok: boolean;
  og_tags_complete: boolean;
  h1_count: number;
  issues: string[];
}

interface SchemaValidationLog {
  id: string;
  timestamp: string;
  url: string;
  valid: boolean;
  schemas_found: string[];
  missing_schemas: string[];
  rich_results_eligible: boolean | null;
  issues: string[];
}

const SeoValidierung = () => {
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<ValidationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [metaLogs, setMetaLogs] = useState<MetaValidationLog[]>([]);
  const [schemaLogs, setSchemaLogs] = useState<SchemaValidationLog[]>([]);

  useEffect(() => {
    loadLogs();
    loadMetaLogs();
    loadSchemaLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const { data, error } = await supabase
        .from("seo_validation_logs")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(30);
      if (error) throw error;
      setLogs((data as unknown as ValidationLog[]) || []);
    } catch (error) {
      console.error("Fehler:", error);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadMetaLogs = async () => {
    try {
      const { data, error } = await supabase
        .from("meta_validation_logs")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(50);
      if (error) throw error;
      setMetaLogs((data as unknown as MetaValidationLog[]) || []);
    } catch (error) {
      console.error("Fehler:", error);
    }
  };

  const loadSchemaLogs = async () => {
    try {
      const { data, error } = await supabase
        .from("schema_validation_logs")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(50);
      if (error) throw error;
      setSchemaLogs((data as unknown as SchemaValidationLog[]) || []);
    } catch (error) {
      console.error("Fehler:", error);
    }
  };

  const runValidation = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-robots');
      if (error) throw error;
      setReport(data);
      if (data.valid) toast.success('✅ Alle Checks bestanden!');
      else toast.warning(`⚠️ ${data.issues.length} Problem(e) gefunden`);
      loadLogs();
    } catch (error: any) {
      toast.error('Fehler: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const runMetaValidation = async () => {
    setLoading(true);
    toast.info("Meta-Tags-Validierung gestartet...");
    try {
      const { data, error } = await supabase.functions.invoke("validate-meta-tags");
      if (error) throw error;
      loadMetaLogs();
      toast.success(`Validiert: ${data?.valid || 0} OK, ${data?.invalid || 0} Probleme`);
    } catch (error: any) {
      toast.error('Fehler: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const runSchemaValidation = async () => {
    setLoading(true);
    toast.info("Schema.org-Validierung gestartet...");
    try {
      const { data, error } = await supabase.functions.invoke("validate-schema-markup");
      if (error) throw error;
      loadSchemaLogs();
      toast.success(`Validiert: ${data?.valid || 0} OK, ${data?.invalid || 0} Probleme`);
    } catch (error: any) {
      toast.error('Fehler: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <Helmet><title>SEO Monitoring - Admin</title></Helmet>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">SEO-Monitoring</h1>
            <p className="text-muted-foreground mt-2">Automatische Validierung von Meta-Tags, Schema.org-Markup und technischem SEO</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={runValidation} disabled={loading} variant="outline" size="sm">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}robots.txt
            </Button>
            <Button onClick={runMetaValidation} disabled={loading} variant="outline" size="sm"><FileText className="h-4 w-4 mr-2" />Meta-Tags</Button>
            <Button onClick={runSchemaValidation} disabled={loading} variant="outline" size="sm"><Code2 className="h-4 w-4 mr-2" />Schema.org</Button>
          </div>
        </div>

        <Tabs defaultValue="robots">
          <TabsList>
            <TabsTrigger value="robots">robots.txt & Sitemap</TabsTrigger>
            <TabsTrigger value="meta">Meta-Tags</TabsTrigger>
            <TabsTrigger value="schema">Schema.org</TabsTrigger>
          </TabsList>

          <TabsContent value="robots" className="space-y-4">
            {report && (
              <Alert variant={report.valid ? "default" : "destructive"}>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Letzte Validierung</AlertTitle>
                <AlertDescription>{report.valid ? "Alle Checks bestanden!" : `${report.issues.length} Probleme`}</AlertDescription>
              </Alert>
            )}
            {logsLoading ? <Card><CardContent className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></CardContent></Card> :
            logs.length > 0 ? (
              <Card><CardHeader><CardTitle>Historie</CardTitle></CardHeader><CardContent>
                <Table><TableHeader><TableRow><TableHead>Zeit</TableHead><TableHead>Status</TableHead><TableHead>URLs</TableHead></TableRow></TableHeader>
                <TableBody>{logs.map((log) => (<TableRow key={log.id}>
                  <TableCell><Clock className="h-4 w-4 inline mr-2" />{format(new Date(log.timestamp), "dd.MM. HH:mm", { locale: de })}</TableCell>
                  <TableCell><Badge variant={log.valid ? "default" : "destructive"}>{log.valid ? "OK" : "Fehler"}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{log.checks.sitemapUrlCount}</Badge></TableCell>
                </TableRow>))}</TableBody></Table>
              </CardContent></Card>
            ) : <Card><CardContent className="py-12 text-center text-muted-foreground">Keine Validierungen</CardContent></Card>}
          </TabsContent>

          <TabsContent value="meta">{metaLogs.length > 0 ? (
            <Card><CardHeader><CardTitle>Meta-Tags</CardTitle></CardHeader><CardContent>
              <Table><TableHeader><TableRow><TableHead>URL</TableHead><TableHead>Title</TableHead><TableHead>Desc</TableHead><TableHead>OG</TableHead><TableHead>H1</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{metaLogs.map((log) => (<TableRow key={log.id}>
                <TableCell className="font-mono text-xs max-w-xs truncate">{log.url}</TableCell>
                <TableCell>{log.title_length_ok ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <AlertTriangle className="h-4 w-4 text-yellow-500" />}</TableCell>
                <TableCell>{log.description_length_ok ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <AlertTriangle className="h-4 w-4 text-yellow-500" />}</TableCell>
                <TableCell>{log.og_tags_complete ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}</TableCell>
                <TableCell>{log.h1_count === 1 ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}</TableCell>
                <TableCell><Badge variant={log.valid ? "default" : "destructive"}>{log.valid ? "OK" : `${log.issues?.length || 0} Fehler`}</Badge></TableCell>
              </TableRow>))}</TableBody></Table>
            </CardContent></Card>
          ) : <Card><CardContent className="py-12 text-center"><FileText className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>Keine Validierungen</p></CardContent></Card>}
          </TabsContent>

          <TabsContent value="schema">{schemaLogs.length > 0 ? (
            <Card><CardHeader><CardTitle>Schema.org</CardTitle></CardHeader><CardContent>
              <Table><TableHeader><TableRow><TableHead>URL</TableHead><TableHead>Schemas</TableHead><TableHead>Fehlend</TableHead><TableHead>Rich</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{schemaLogs.map((log) => (<TableRow key={log.id}>
                <TableCell className="font-mono text-xs max-w-xs truncate">{log.url}</TableCell>
                <TableCell><div className="flex gap-1 flex-wrap">{log.schemas_found?.map((s: string) => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}</div></TableCell>
                <TableCell>{log.missing_schemas?.length > 0 ? <div className="flex gap-1">{log.missing_schemas.map((s: string) => <Badge key={s} variant="destructive" className="text-xs">{s}</Badge>)}</div> : "-"}</TableCell>
                <TableCell>{log.rich_results_eligible === true ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : log.rich_results_eligible === false ? <XCircle className="h-4 w-4 text-red-500" /> : "-"}</TableCell>
                <TableCell><Badge variant={log.valid ? "default" : "destructive"}>{log.valid ? "OK" : `${log.issues?.length} Fehler`}</Badge></TableCell>
              </TableRow>))}</TableBody></Table>
            </CardContent></Card>
          ) : <Card><CardContent className="py-12 text-center"><Code2 className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>Keine Validierungen</p></CardContent></Card>}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default SeoValidierung;
